import React, { useState, useEffect, useCallback } from 'react';
import WhatsAppFlowBuilder from './WhatsAppFlowBuilder';
import WhatsAppConversations from './WhatsAppConversations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

async function readJson(res) {
  try { return await res.clone().json(); } catch {}
  try { return await res.json(); } catch {}
  try { const t = await res.text(); return t ? JSON.parse(t) : null; } catch { return null; }
}

const LEAD_STATUS_OPTIONS = [
  { value: 'new', label: 'New Lead (Inquiry)' },
  { value: 'support', label: 'Support Request' },
  { value: 'complaint', label: 'Complaint' },
];

export const WhatsAppBotEditor = ({ token }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [expanded, setExpanded] = useState(null); // option index whose flow is open
  const [welcomeOpen, setWelcomeOpen] = useState(false);

  const authHeaders = useCallback(() => ({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cRes = await fetch(`${API_URL}/api/whatsapp/bot-config`, { headers: authHeaders() });
      const cData = await readJson(cRes);
      if (cData && cData.config) {
        const cfg = cData.config;
        if (!cfg.flows) cfg.flows = {};
        if (!cfg.flows.welcome) cfg.flows.welcome = { final_message: '', steps: [] };
        if (!cfg.start_mode) cfg.start_mode = 'flow';
        if (!cfg.welcome_flow_id) cfg.welcome_flow_id = 'welcome';
        setConfig(cfg);
      }
    } catch (e) { setMsg({ type: 'error', text: 'Load नहीं हो पाया' }); }
    setLoading(false);
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const updateOption = (idx, field, value) => {
    setConfig(c => {
      const options = c.options.map((o, i) => i === idx ? { ...o, [field]: value } : o);
      let flows = c.flows || {};
      if (field === 'mode' && value === 'flow') {
        const oid = options[idx].id;
        if (!flows[oid]) flows = { ...flows, [oid]: { final_message: '', steps: [] } };
      }
      return { ...c, options, flows };
    });
  };
  const addOption = () => {
    if (config.options.length >= 10) return;
    setConfig(c => ({ ...c, options: [...c.options, { id: `menu_${Date.now()}`, title: 'नया Option', description: '', reply: '', lead_status: 'new', mode: 'reply' }] }));
  };
  const removeOption = (idx) => setConfig(c => {
    const removed = c.options[idx];
    const flows = { ...(c.flows || {}) };
    if (removed && flows[removed.id]) delete flows[removed.id];
    return { ...c, options: c.options.filter((_, i) => i !== idx), flows };
  });

  const updateFlow = (optId, newFlow) => setConfig(c => ({ ...c, flows: { ...(c.flows || {}), [optId]: newFlow } }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/bot-config`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(config),
      });
      const data = await readJson(res);
      if (data && data.success) {
        setMsg({ type: 'success', text: 'Save हो गया ✅ — अब WhatsApp पर नया bot चलेगा' });
        if (data.config) { const cfg = data.config; if (!cfg.flows) cfg.flows = {}; setConfig(cfg); }
      } else {
        setMsg({ type: 'error', text: (data && (data.detail || data.error)) || 'Save failed' });
      }
    } catch (e) { setMsg({ type: 'error', text: 'Save failed: ' + e.message }); }
    setSaving(false);
  };

  if (loading || !config) {
    return <div className="bg-white rounded-xl p-10 border text-center text-gray-400" data-testid="bot-editor-loading"><i className="fa-solid fa-spinner animate-spin text-2xl"></i><p className="mt-2">Loading...</p></div>;
  }

  return (
    <div className="space-y-4" data-testid="whatsapp-bot-editor">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-green-600 rounded-xl p-5 text-white">
        <h3 className="text-xl font-bold flex items-center gap-2"><i className="fa-brands fa-whatsapp"></i> WhatsApp Bot Builder</h3>
        <p className="text-sm text-white/90 mt-1">School owner आपको WhatsApp पर message करेगा → ये menu जाएगा। हर option या तो सीधा जवाब देगा या एक multi-step सवाल-जवाब flow चलाएगा। सब कुछ यहीं से बदलें — code नहीं छूना पड़ेगा।</p>
      </div>

      {msg && (
        <div className={`rounded-xl px-4 py-3 text-sm ${msg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="bot-editor-msg">{msg.text}</div>
      )}

      {/* Welcome / Verify-User flow */}
      <div className="bg-white rounded-xl p-5 border space-y-3" data-testid="welcome-flow-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h4 className="font-bold text-gray-800">🚀 Welcome / Verify Flow</h4>
            <p className="text-xs text-gray-500 mt-0.5">जब कोई पहली बार "hi" भेजे — पहले ये flow चलेगा (school verify → UDISE → intro → menu)।</p>
          </div>
          <div className="flex gap-2 text-sm">
            <button onClick={() => setConfig(c => ({ ...c, start_mode: 'flow' }))}
              className={`px-3 py-1.5 rounded-lg font-medium border-2 ${config.start_mode === 'flow' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200'}`} data-testid="start-mode-flow">
              Flow चालू
            </button>
            <button onClick={() => setConfig(c => ({ ...c, start_mode: 'menu' }))}
              className={`px-3 py-1.5 rounded-lg font-medium border-2 ${config.start_mode === 'menu' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200'}`} data-testid="start-mode-menu">
              सीधा Menu
            </button>
          </div>
        </div>
        {config.start_mode === 'flow' && (
          <>
            <button onClick={() => setWelcomeOpen(o => !o)}
              className="w-full px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-medium flex items-center justify-between hover:bg-indigo-100" data-testid="welcome-flow-toggle">
              <span><i className="fa-solid fa-diagram-project"></i> Welcome Flow Builder ({(config.flows.welcome?.steps || []).length} steps)</span>
              <i className={`fa-solid fa-chevron-${welcomeOpen ? 'up' : 'down'}`}></i>
            </button>
            {welcomeOpen && (
              <WhatsAppFlowBuilder flow={config.flows.welcome || { final_message: '', steps: [] }} onChange={(nf) => updateFlow('welcome', nf)} />
            )}
          </>
        )}
      </div>


      {/* Test Mode */}
      <div className="bg-white rounded-xl p-5 border space-y-3" data-testid="test-mode-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h4 className="font-bold text-gray-800">🧪 Test Mode</h4>
            <p className="text-xs text-gray-500 mt-0.5">ON करने पर नीचे दिए numbers (+ सभी admin numbers) हर बार पूरा fresh flow देखेंगे — "पुनः स्वागत" नहीं आएगा। बार-बार delete करने की ज़रूरत नहीं।</p>
          </div>
          <button onClick={() => setConfig(c => ({ ...c, test_mode: !c.test_mode }))}
            className={`px-5 py-1.5 rounded-lg font-bold border-2 ${config.test_mode ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-500 border-gray-200'}`}
            data-testid="test-mode-toggle">
            {config.test_mode ? 'ON' : 'OFF'}
          </button>
        </div>
        {config.test_mode && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Test numbers (comma से अलग — optional; admin numbers अपने-आप शामिल हैं)</label>
            <input
              value={(config.test_numbers || []).join(', ')}
              onChange={e => setConfig(c => ({ ...c, test_numbers: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
              placeholder="9826618603, 9812345678"
              className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="test-numbers-input" />
            <p className="text-[11px] text-amber-600 mt-1">⚠️ Live launch के समय इसे OFF कर दें।</p>
          </div>
        )}
      </div>

      {/* Greeting + button + footer */}
      <div className="bg-white rounded-xl p-5 border space-y-4">
        <h4 className="font-bold text-gray-800">Menu की शुरुआत</h4>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Greeting message (menu के ऊपर)</label>
          <textarea value={config.greeting} onChange={e => setConfig({ ...config, greeting: e.target.value })} rows={3}
            className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-emerald-500 outline-none resize-none text-sm" data-testid="bot-greeting-input" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Menu button text</label>
            <input value={config.menu_button} onChange={e => setConfig({ ...config, menu_button: e.target.value })} maxLength={20}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-emerald-500 outline-none text-sm" data-testid="bot-button-input" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Footer text</label>
            <input value={config.footer} onChange={e => setConfig({ ...config, footer: e.target.value })} maxLength={60}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-emerald-500 outline-none text-sm" data-testid="bot-footer-input" />
          </div>
        </div>
      </div>

      {/* Options */}
      <div className="bg-white rounded-xl p-5 border space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-bold text-gray-800">Menu Options ({config.options.length})</h4>
          <button onClick={addOption} disabled={config.options.length >= 10}
            className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1" data-testid="bot-add-option-btn">
            <i className="fa-solid fa-plus"></i> Option जोड़ें
          </button>
        </div>
        {config.options.map((opt, idx) => {
          const isFlow = opt.mode === 'flow';
          const flow = (config.flows || {})[opt.id] || { final_message: '', steps: [] };
          return (
            <div key={opt.id || idx} className="border-2 border-gray-100 rounded-xl p-4 space-y-3 bg-gray-50/50" data-testid={`bot-option-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-emerald-600">Option {idx + 1}</span>
                <button onClick={() => removeOption(idx)} className="text-red-400 hover:text-red-600 text-sm" data-testid={`bot-remove-option-${idx}`}><i className="fa-solid fa-trash-can"></i></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Button Title (24 अक्षर तक)</label>
                  <input value={opt.title} maxLength={24} onChange={e => updateOption(idx, 'title', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid={`bot-option-title-${idx}`} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">यह किस लिए है? (CRM tag)</label>
                  <select value={opt.lead_status} onChange={e => updateOption(idx, 'lead_status', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid={`bot-option-status-${idx}`}>
                    {LEAD_STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Short description (menu में title के नीचे)</label>
                <input value={opt.description} maxLength={72} onChange={e => updateOption(idx, 'description', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid={`bot-option-desc-${idx}`} />
              </div>

              {/* Mode toggle */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">इस option पर क्या हो?</label>
                <div className="flex gap-2">
                  <button onClick={() => updateOption(idx, 'mode', 'reply')}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border-2 ${!isFlow ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200'}`}
                    data-testid={`bot-option-mode-reply-${idx}`}>
                    💬 सीधा जवाब
                  </button>
                  <button onClick={() => updateOption(idx, 'mode', 'flow')}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border-2 ${isFlow ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200'}`}
                    data-testid={`bot-option-mode-flow-${idx}`}>
                    🔀 Multi-step Flow
                  </button>
                </div>
              </div>

              {!isFlow ? (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Auto-reply message (यह select करने पर जाएगा)</label>
                  <textarea value={opt.reply} rows={3} onChange={e => updateOption(idx, 'reply', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm resize-none" data-testid={`bot-option-reply-${idx}`} />
                </div>
              ) : (
                <div className="space-y-2">
                  <button onClick={() => setExpanded(expanded === idx ? null : idx)}
                    className="w-full px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-medium flex items-center justify-between hover:bg-indigo-100"
                    data-testid={`bot-option-edit-flow-${idx}`}>
                    <span><i className="fa-solid fa-diagram-project"></i> Flow Builder ({flow.steps.length} सवाल)</span>
                    <i className={`fa-solid fa-chevron-${expanded === idx ? 'up' : 'down'}`}></i>
                  </button>
                  {expanded === idx && (
                    <div className="pt-1">
                      <WhatsAppFlowBuilder flow={flow} onChange={(nf) => updateFlow(opt.id, nf)} />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <button onClick={save} disabled={saving}
          className="w-full py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="bot-save-btn">
          {saving ? <><i className="fa-solid fa-spinner animate-spin"></i> Saving...</> : <><i className="fa-solid fa-floppy-disk"></i> पूरा Bot Save करें</>}
        </button>
      </div>

      {/* WhatsApp conversations — view & delete (test reset) */}
      <WhatsAppConversations token={token} />
    </div>
  );
};

export default WhatsAppBotEditor;
